import React, { useCallback } from 'react';
import _some from 'lodash/some';
import _isEmpty from 'lodash/isEmpty';
import _includes from 'lodash/includes';
import { useDispatch, useMappedState } from 'redux-react-hook';
import { STRINGS } from 'config';
import { updateCompoundAnswers } from 'redux/actions/consultation/answer';
import CompoundAnswer from 'styles/blocks/compound';
import { consultationSelector } from 'redux/selectors/consultation';
import { css } from 'styled-components';
import { CompoundBody } from './compoundBody';
import { CompoundConfirmContactDetails } from './confirmContactDetails';
import { NextButton } from '../nextButton';
import { addressBodyContainerStyles, calculateAnswerStyles, CompoundAddress } from './address';
import { CompoundPatientDetails } from './patientDetails';

const { TAGS } = STRINGS;
const { LAST_PAGE_SUBMIT } = TAGS.QUESTION;

const calculateDefaultAnswerStyles = index => css`
  ${index === 0 ? 'margin-left: 0' : ''};
  flex-basis: 50%;
`;

const containerStyleSplit = css`
  display: flex;
  flex-wrap: wrap;
`;

export function Compound({ question, isMounted, onClickNextQuestion, validationErrors }) {
  const mapState = useCallback(state => consultationSelector(state), []);
  const { compoundAnswers, inputsReadOnly, isClinicalReviewMode } = useMappedState(mapState);

  // actions to update value
  const dispatch = useDispatch();

  function handleUpdateCompoundAnswer(answer) {
    return dispatch(updateCompoundAnswers(answer));
  }

  const renderCompoundAnswerType = () => {
    const isNextButtonDisabled = _some(compoundAnswers, answer => {
      _isEmpty(answer.value);
    });
    const showSubmitInsteadOfNext = _includes(question.tags, LAST_PAGE_SUBMIT);
    if (_includes(question.tags, TAGS.QUESTION.STRUCTURED_ADDRESS)) {
      return (
        <CompoundAddress
          question={question}
          onClickNextQuestion={onClickNextQuestion}
          compoundAnswers={compoundAnswers}
          updateCompoundAnswers={answers => dispatch(updateCompoundAnswers(answers))}
          handleUpdateCompoundAnswer={handleUpdateCompoundAnswer}
          isReadOnly={inputsReadOnly}
        >
          <CompoundBody
            question={question}
            compoundAnswers={compoundAnswers}
            handleUpdateCompoundAnswer={handleUpdateCompoundAnswer}
            calculateAnswerStyles={calculateAnswerStyles}
            containerStyles={addressBodyContainerStyles}
            isReadOnly={inputsReadOnly}
            validationErrors={validationErrors}
          />
        </CompoundAddress>
      );
    }
    if (_includes(question.tags, TAGS.QUESTION.STRUCTURED_CONTACT_DETAILS)) {
      return (
        <CompoundConfirmContactDetails
          question={question}
          onClickNextQuestion={onClickNextQuestion}
          validationErrors={validationErrors}
          compoundAnswers={compoundAnswers}
          handleUpdateCompoundAnswer={handleUpdateCompoundAnswer}
          isReadOnly={inputsReadOnly}
        />
      );
    }
    if (_includes(question.tags, TAGS.QUESTION.STRUCTURED_PATIENT_DETAILS)) {
      return (
        <CompoundPatientDetails
          question={question}
          validationErrors={validationErrors}
          compoundAnswers={compoundAnswers}
          handleUpdateCompoundAnswer={handleUpdateCompoundAnswer}
          calculateAnswerStyles={calculateDefaultAnswerStyles}
          isReadOnly={inputsReadOnly}
          isMounted={isMounted}
          isClinicalReviewMode={isClinicalReviewMode}
          onClickNextQuestion={onClickNextQuestion}
          isNextButtonDisabled={isNextButtonDisabled}
          showSubmitInsteadOfNext={showSubmitInsteadOfNext}
        />
      );
    }
    return (
      <CompoundAnswer>
        <CompoundBody
          question={question}
          validationErrors={validationErrors}
          compoundAnswers={compoundAnswers}
          handleUpdateCompoundAnswer={handleUpdateCompoundAnswer}
          calculateAnswerStyles={calculateDefaultAnswerStyles}
          isReadOnly={inputsReadOnly}
          isMounted={isMounted}
          containerStyles={containerStyleSplit}
          isClinicalReviewMode={isClinicalReviewMode}
        />
        <NextButton
          dataAutomation={`nextButton-${question.questionId}`}
          onClick={onClickNextQuestion}
          isDisabled={isNextButtonDisabled}
        >
          {showSubmitInsteadOfNext && 'Submit'}
        </NextButton>
      </CompoundAnswer>
    );
  };
  return (
    <CompoundAnswer>
      {renderCompoundAnswerType()}
    </CompoundAnswer>
  );
}
